import React, { useState } from "react";
import { buildPayLink, isLinkablePlatform, platformLabel } from "../payLinks";

export const PAY_SAFETY_LINE =
  "Payments happen in the seller's own app, not on du Pont Circle. Each app's own security and two-step checks protect the payment.";

// True when PayOptions will draw something for this platform + handle, so
// callers can skip a heading that would otherwise sit over nothing.
export function hasPayOptions(platform, handle) {
  if (!platform) return false;
  if (platform === "cash" || platform === "check") return true;
  return typeof handle === "string" && handle.trim() !== "";
}

/**
 * How to pay for a listing.
 *
 * Venmo / Cash App / PayPal: a button the SITE builds from the handle (see
 * payLinks.js). If no safe link can be made, the handle is shown as text.
 * Zelle: no public link exists, so the seller's Zelle email/phone with Copy.
 * Cash / Check: a plain line.
 */
function PayOptions({ platform, handle }) {
  const [copied, setCopied] = useState(false);
  if (!hasPayOptions(platform, handle)) return null;

  const label = platformLabel(platform);
  const trimmed = typeof handle === "string" ? handle.trim() : "";
  const link = isLinkablePlatform(platform) ? buildPayLink(platform, trimmed) : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  let body;
  if (link) {
    body = (
      <div className="flex flex-col gap-y-[6px]">
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-x-[8px] w-full sm:w-auto rounded-[10px] bg-dupontBlue dark:bg-darkGold text-white dark:text-dupontBlue px-[20px] py-[12px] text-[16px] font-display font-semibold no-underline hover:opacity-90 transition-opacity"
        >
          Pay with {link.label}
          <span aria-hidden="true">&#8599;</span>
          <span className="sr-only">(opens the {link.label} app or site in a new tab)</span>
        </a>
        <div className="text-[14px] font-body text-dupontGray dark:text-darkTextSecondary">
          Seller's {link.label}: <span className="font-semibold text-dupontBlue dark:text-darkText">{link.display}</span>
        </div>
      </div>
    );
  } else if (platform === "zelle" && trimmed) {
    body = (
      <div className="flex flex-col gap-y-[6px]">
        <div className="text-[15px] font-body text-dupontBlue dark:text-darkText">
          Pay with Zelle to:
        </div>
        <div className="flex flex-row flex-wrap items-center gap-[10px]">
          <span className="font-semibold text-[16px] text-dupontBlue dark:text-darkText break-all">
            {trimmed}
          </span>
          <button
            type="button"
            onClick={copy}
            className="rounded-[8px] border border-dupontGold px-[12px] py-[6px] text-[14px] font-semibold text-dupontBlue dark:text-darkGold hover:bg-dupontGold/10 cursor-pointer"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <span className="sr-only" aria-live="polite">{copied ? "Copied to clipboard" : ""}</span>
        </div>
        <div className="text-[14px] font-body text-dupontGray dark:text-darkTextSecondary">
          Open Zelle in your bank's app and send to this email or phone.
        </div>
      </div>
    );
  } else if (platform === "cash" || platform === "check") {
    body = (
      <div className="text-[15px] font-body text-dupontBlue dark:text-darkText">
        The seller takes {platform === "cash" ? "cash" : "a check"}
        {trimmed ? <> &mdash; <span className="break-all">{trimmed}</span></> : null}. Arrange it with them directly.
      </div>
    );
  } else {
    // A linkable app whose handle could not be turned into a safe link:
    // show it as text only, never as a link.
    body = (
      <div className="text-[15px] font-body text-dupontBlue dark:text-darkText">
        {label || "Payment"}: <span className="font-semibold break-all">{trimmed}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-[8px] rounded-[12px] border border-dupontGold/40 bg-dupontCream/60 dark:bg-darkElevated p-[14px]">
      <div className="text-[13px] font-bold uppercase tracking-wide text-dupontGray dark:text-darkTextSecondary">
        How to pay
      </div>
      {body}
      <p className="m-0 text-[13px] font-body text-dupontGray dark:text-darkTextSecondary leading-snug">
        {platform === "cash" || platform === "check"
          ? "du Pont Circle never handles payments."
          : PAY_SAFETY_LINE}
      </p>
    </div>
  );
}

export default PayOptions;
